import { AppConfig } from './app-config.model';

export const DEFAULT_APP_CONFIG: AppConfig = {
  appInfo: {
    title: 'Miyar Dashboard',
    description: 'Minimalist investment fund & portfolio management platform'
  },
  branding: {
    logos: {
      primary: 'assets/image/miyar-logo.png',
      primarySvg: 'assets/image/miyar-logo.png',
      loginSideImage: 'assets/image/page-side-img.jpg'
    },
    favicon: 'assets/image/miyar-logo.png',
    defaultAvatar: 'assets/image/default.jpg'
  },
  theme: {
    baseTokens: {
      spacingUnit: '4px',
      borderRadius: '20px',
      fontBaseSize: '14px',
      controlHeightBase: '40px'
    },
    typography: {
      fontFamilyPrimary: "'Tajawal', sans-serif",
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&display=swap',
      scale: {
        displayLarge: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.714)', weight: '700' },
        headlineLarge: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.428)' },
        headlineMedium: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.285)' },
        titleLarge: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.142)' },
        titleMedium: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.071)' },
        bodyLarge: { size: 'calc(var(--sys-base-tokens-font-base-size) * 1.000)', weight: '400' },
        bodyMedium: { size: 'calc(var(--sys-base-tokens-font-base-size) * 0.928)' },
        bodySmall: { size: 'calc(var(--sys-base-tokens-font-base-size) * 0.857)' },
        caption: { size: 'calc(var(--sys-base-tokens-font-base-size) * 0.785)' }
      }
    },
    spacing: {
      '1': 'calc(var(--sys-base-tokens-spacing-unit) * 1)',
      '2': 'calc(var(--sys-base-tokens-spacing-unit) * 2)',
      '3': 'calc(var(--sys-base-tokens-spacing-unit) * 3)',
      '4': 'calc(var(--sys-base-tokens-spacing-unit) * 4)',
      '5': 'calc(var(--sys-base-tokens-spacing-unit) * 5)',
      '6': 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
      '8': 'calc(var(--sys-base-tokens-spacing-unit) * 8)'
    },
    sizing: {
      iconSm: 'calc(var(--sys-base-tokens-spacing-unit) * 4)',
      iconMd: 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
      iconLg: 'calc(var(--sys-base-tokens-spacing-unit) * 8)',
      avatarSm: 'calc(var(--sys-base-tokens-spacing-unit) * 8)',
      avatarMd: 'calc(var(--sys-base-tokens-spacing-unit) * 10)',
      avatarLg: 'calc(var(--sys-base-tokens-spacing-unit) * 12)',
      buttonSm: 'calc(var(--sys-base-tokens-control-height-base) * 0.8)',
      buttonMd: 'calc(var(--sys-base-tokens-control-height-base) * 1.0)',
      buttonLg: 'calc(var(--sys-base-tokens-control-height-base) * 1.2)'
    },
    colors: {
      primary: '#10B981',
      primaryContrast: 'var(--sys-colors-surface)',
      accent: '#059669',
      warn: '#EF4444',
      background: '#F4F7FB',
      surface: '#FFFFFF',
      surfaceVariant: '#E2E8F0',
      textPrimary: '#0F172A',
      textSecondary: '#475569',
      textMuted: '#94A3B8',
      textDisabled: '#CBD5E1',
      disabledBg: '#F1F3F7',
      disabledCheckboxBg: '#F9FAFC',
      bgOverlay: '#191B2180',
      highlight: '#00000000',
      highlightHover: '#0000000D',
      highlightPressed: '#00000012',
      bgBrandHover: '#003FA3',
      bgBrandSecondary: '#D0E2FF',
      textHeading: '#020617',
      textPlaceholder: 'var(--sys-colors-text-muted)',
      borderLight: '#F1F5F9',
      borderMedium: '#CBD5E1',
      borderStripe: '#F8FAFC',
      borderNavbar: 'var(--sys-colors-surface-variant)',
      status: {
        successBg: '#DCFCE7',
        successBorder: 'var(--sys-colors-primary)',
        successText: '#065F46',
        warningBg: '#FEF9C3',
        warningBorder: '#EAB308',
        errorBg: '#FEE2E2',
        errorBorder: 'var(--sys-colors-warn)',
        errorAccent: '#DC2626'
      },
      button: {
        primaryBg: 'var(--sys-colors-primary)',
        primaryText: 'var(--sys-colors-surface)',
        cancelBg: 'var(--sys-colors-border-light)',
        cancelText: 'var(--sys-colors-text-secondary)',
        cancelHoverBg: 'var(--sys-colors-surface-variant)',
        dangerBg: 'var(--sys-colors-status-error-bg)',
        dangerText: 'var(--sys-colors-status-error-accent)',
        rejectBg: 'var(--sys-colors-status-error-accent)',
        rejectText: 'var(--sys-colors-surface)',
        defaultBg: 'var(--sys-colors-surface)'
      },
      chart: {
        green: 'var(--sys-colors-primary)',
        red: 'var(--sys-colors-warn)',
        active: 'var(--sys-colors-primary)'
      },
      misc: {
        scrollbarThumb: 'var(--sys-colors-text-muted)'
      }
    },
    shape: {
      borderRadius: {
        xs: 'calc(var(--sys-base-tokens-border-radius) * 0.1)',
        sm: 'calc(var(--sys-base-tokens-border-radius) * 0.2)',
        md: 'calc(var(--sys-base-tokens-border-radius) * 0.3)',
        lg: 'calc(var(--sys-base-tokens-border-radius) * 0.4)',
        xl: 'calc(var(--sys-base-tokens-border-radius) * 0.5)',
        xxl: 'calc(var(--sys-base-tokens-border-radius) * 0.6)',
        pill: '9999px'
      }
    },
    elevation: {
      level1: '0 1px 3px -1px rgba(0, 82, 255, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
      level2: '0 2px 8px -2px rgba(0, 82, 255, 0.15)',
      levelInset: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
    },
    motion: {
      durationFast: '100ms',
      durationNormal: '150ms',
      durationMedium: '250ms',
      durationSlow: '350ms',
      easingStandard: 'cubic-bezier(0.4, 0.0, 0.2, 1)'
    },
    layout: {
      sidebarCollapsedWidth: 'calc(var(--sys-base-tokens-control-height-base) * 1.95)',
      navbarHeight: 'calc(var(--sys-base-tokens-control-height-base) * 1.75)'
    },
    components: {
      navbar: {
        background: 'var(--sys-colors-surface)'
      },
      sidebar: {
        background: 'var(--sys-colors-surface)'
      },
      toaster: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        positionBottom: 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
        positionLeftPercent: '50%'
      },
      table: {
        headerBg: 'var(--sys-colors-border-stripe)',
        headerTextColor: 'var(--sys-colors-text-heading)',
        headerFontWeight: 'var(--sys-typography-scale-display-large-weight)',
        stripeBg: 'var(--sys-colors-border-light)',
        textColor: 'var(--sys-colors-text-primary)',
        fontWeight: 'var(--sys-typography-scale-body-large-weight)'
      },
      input: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        borderWidth: '1px',
        sizes: {
          large: {
            height: 'var(--sys-sizing-button-lg)',
            padding: '0 var(--sys-spacing-4)',
            fontSize: 'var(--sys-typography-scale-body-large-size)'
          }
        },
        variants: {
          default: {
            bg: 'var(--sys-colors-surface)',
            border: 'var(--sys-colors-border-medium)',
            text: 'var(--sys-colors-text-primary)',
            placeholder: 'var(--sys-colors-text-placeholder)',
            focusBorder: 'var(--sys-colors-primary)',
            focusShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)'
          }
        }
      },
      button: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        sizes: {
          large: {
            height: 'var(--sys-sizing-button-lg)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 3)',
            padding: '0 var(--sys-spacing-6)',
            fontSize: 'var(--sys-typography-scale-body-large-size)'
          },
          medium: {
            height: 'var(--sys-sizing-button-md)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 2.5)',
            padding: '0 var(--sys-spacing-4)',
            fontSize: 'var(--sys-typography-scale-body-large-size)'
          },
          small: {
            height: 'var(--sys-sizing-button-sm)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 2)',
            padding: '0 var(--sys-spacing-3)',
            fontSize: 'var(--sys-typography-scale-body-medium-size)'
          }
        },
        variants: {
          primary: {
            bg: 'var(--sys-colors-button-primary-bg)',
            text: 'var(--sys-colors-button-primary-text)',
            hoverBg: 'var(--sys-colors-bg-brand-hover)',
            hoverText: 'var(--sys-colors-button-primary-text)'
          },
          cancel: {
            bg: 'var(--sys-colors-button-cancel-bg)',
            text: 'var(--sys-colors-button-cancel-text)',
            border: '1px solid var(--sys-colors-border-medium)',
            hoverBg: 'var(--sys-colors-button-cancel-hover-bg)',
            hoverText: 'var(--sys-colors-text-heading)'
          },
          default: {
            bg: 'var(--sys-colors-button-default-bg)',
            text: 'var(--sys-colors-primary)',
            border: '1px solid var(--sys-colors-primary)',
            hoverBg: 'var(--sys-colors-border-light)',
            hoverText: 'var(--sys-colors-primary)'
          },
          success: {
            bg: 'var(--sys-colors-status-success-bg)',
            text: 'var(--sys-colors-status-success-text)',
            hoverBg: 'var(--sys-colors-status-success-border)',
            hoverText: 'var(--sys-colors-status-success-text)'
          },
          danger: {
            bg: 'var(--sys-colors-button-danger-bg)',
            text: 'var(--sys-colors-button-danger-text)',
            hoverBg: 'var(--sys-colors-button-reject-bg)',
            hoverText: 'var(--sys-colors-button-reject-text)'
          }
        }
      }
    }
  }
};
